import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { usePrevious } from 'react-use';

import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { resolveCoaForBusinessChange } from 'app-components/flux/categories/categoriesUtils';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
// Import after transactions: receiptsSelectors depends on transactionsSelectors, and loading
// receipts first triggers the transactions → investment-holdings → accounts module cycle.
import { receiptsActions, receiptsSelectors } from 'app-components/flux/receipts';
import {
  applyDefaultBusinessIdToSplitItems,
  isAnySplitTransferCounterpart,
  omitLinkedItemsFromSplit,
  preserveSplitTransferGuardedFields,
  reconcileSplitLinkedItems,
  splitItemsToArray,
  stripDeadBillableLinks,
  type ReceiptSplitSeed,
} from 'app-components/flux/transactions/splitsHelpers';
// FLUX
import { getInvoices } from 'app-components/flux/transactions/invoicesSelectors';
import { getLogger, tracker } from 'app-components/utils/core';
import { InvoicePaymentUpsertAction } from 'components/InvoicePayment/actions';
import type { RecordOf } from 'immutable';
import { Map as ImmutableMap, List, Record } from 'immutable';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LinkIcon from '@mui/icons-material/Link';
import OpenNewIcon from '@mui/icons-material/OpenInNewRounded';
import RepeatOneIcon from '@mui/icons-material/RepeatOneRounded';
import RepeatIcon from '@mui/icons-material/RepeatRounded';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';

import { ConnectionStatusFooter } from 'components/ConnectionStatus';
import QTip from 'components/QuickenControls/QTip';
import DevMenuButton from 'components/DevMenuButton';

// MUI
import { makeStyles } from 'tss-react/mui';

import { rootUiActions } from 'app-components/flux/root-ui';
import { rootUiTypes } from 'app-components/flux/root-ui';
// DATA
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import { showViewSeriesDialog } from 'components/Dialogs/ViewSeriesDialog/actions';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { isSupportedTransferTxn } from 'data/transactions/utils';
import { useDispatch } from 'react-redux';

import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import { dateSelectors } from 'app-components/flux/date';
import QIllustration from 'components/QIllustration';
import AttachmentsField from 'components/AttachmentsField';
import ClientProjectSelect, { type ProjectSelect, useOrnamentType } from 'components/ClientProjectSelect';
import LoadingDialog from 'components/Dialogs/LoadingDialog';
import ReviewDialog from 'components/Dialogs/ScheduledTransactionAddDialog/ReviewDialog';
import SelectScheduledTransactionDialog from 'components/Dialogs/SelectScheduledTransactionDialog';
import { getTotalNumberOfSplitsWithAllocations } from 'data/transactionList/selectors';

import { Box } from '@mui/material';
import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import type { ChartOfAccount, ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { customersSelectors } from 'app-components/flux/customers';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { projectsSelectors } from 'app-components/flux/projects';
import type {
  ScheduledTransaction,
  ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import type { Tag } from 'app-components/flux/tags/tagsTypes';
import type {
  Attachment,
  CashFlowTransaction,
  QcsColor,
  SplitProps,
  TransactionProps,
} from 'app-components/flux/transactions/transactionsTypes';
import {
  isInvoiceTxn,
  isManualTransaction,
  isRefundTxn,
  isUITransferTxn,
} from 'app-components/flux/transactions/transactionsUtils';
import InvoiceZeroSvg from 'assets/illustrations/Invoice-Paid-Amount-1.svg';
// CUSTOM COMPONENTS
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import { TransactionRuleAction } from 'components/Dialogs/TransactionRules/actions';
import Dump from 'components/Dump';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import QPanelButton from 'components/QPanelButton';
import AccountField from 'components/QuickenControls/AccountField';
import SPayeeField from 'components/SPayeeField';
import ScheduledTransactionEditDialog from 'components/Dialogs/ScheduledTransactionEditDialog';
import BoxField from 'components/Transactions/BoxField';
import DateField from 'components/Transactions/DateField';
import RefundLine from 'components/Transactions/RefundLine';
import ReceiptSection from 'components/Transactions/ReceiptSection';
import { repairReceiptAttachmentSources } from 'components/Transactions/ReceiptSection/receiptTransaction';
import { showSplitDialog } from 'components/Transactions/SplitDialog/actions';
import TxnHeaderSection from 'components/Transactions/TxnHeaderSection';
import UnsavedChangesDialog from 'components/Transactions/UnsavedChangesDialog';
import VisibilitySection from 'components/Transactions/VisibilitySection';
import {
  getExcludedFromReportsDisplayState,
  getExcludedFromSpendingPlanDisplayState,
} from 'components/Transactions/VisibilitySection/utils';
import ZeroStateView from 'components/ZeroStateView';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';
import { useUrlDialog } from 'hooks/useUrlDialog';
import { dispatchRequestDialog } from 'components/RequestDialog/dispatcher';

import dispatchGeneratorDialog from 'components/Transactions/GeneratorDialog/dispatcher';

import CategorySection from '../components/CategorySection';
import { OtherSideField } from 'components/Transactions/OtherSideField';
import type { TransactionEditDialogProps, ResourceTxnAction } from '../types';
import { showTxnDetailsDialog } from '../actions';
import { getTxnTransferHasAllocations, isArAssociatedPaymentTxn } from '../selectors';

// assets

// import { incomeTxn, expenseTxn } from './testData';

const log = getLogger('component/Dialogs/TransactionEditDialog/legacy/index.tsx');

const useStyles = makeStyles()((theme) => ({
  visibilitySection: {
    display: 'flex',
    flexDirection: 'column',
    '> p': {
      padding: 0,
      margin: 0,
    },
    gap: theme.spacing(1),
  },
  spacerRow: {
    marginTop: theme.spacing(1.5),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(3),
    '& > .MuiBox-root': {
      width: `calc(100% + ${theme.spacing(15)})`,
      paddingRight: theme.spacing(13.25),
      '> div': {
        gap: theme.spacing(3),
      },
    },
  },
  paymentId: {
    margin: `${theme.spacing(1)} 0px`,
    '& .MuiInputBase-input': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  link: {
    color: theme.palette.primary.main,
    maxWidth: 223,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  openIcon: {
    height: 14,
    width: 14,
    marginBottom: 2,
    marginLeft: 2,
  },
  thinRow: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(3),
    '.MuiFormControl-root': {
      margin: 0,
    },
  },
  payeeField: {
    display: 'flex',
    flex: 2,
    margin: 0,
  },
  dateField: {
    flex: 1,
  },
  refundLine: {
    display: 'flex',
    justifyContent: 'space-between',
    textAlign: 'right',
    paddingLeft: 14,
  },
  refundAfterAmountRow: {
    marginTop: -24,
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: theme.palette.divider,
  },
  bottomSection: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 24,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    '> div': {
      width: '100%',
    },
  },
  recurringRoot: {
    padding: theme.spacing(3),
    display: 'flex',
    minHeight: 464,
  },
  recurringColumn: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(2),
  },
  recurringSubtitle: {
    paddingBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  createRuleButton: {
    marginRight: theme.spacing(1),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
  },
  updateButton: {
    marginLeft: 0,
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  editSplitAmount: {
    margin: 0,
    padding: 0,
    height: 58,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: '0px -1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  dividerSection: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  clientProjectSelect: {},
  notesAndAttachmentsSetWidth: {
    maxWidth: 348,
    gap: theme.spacing(2),
  },
  dialogIllustration: {
    width: '100px',
    height: '100px',
  },
  dialogContent: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(5),
  },
  dialogContentTextWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

function clickPayeeLink(name = '') {
  window.open(`https://www.google.com/search?q=${encodeURIComponent(name)}`);
}

const defaultTxn = transactionsTypes.mkCashFlowTransaction({ state: 'CLEARED' });

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Dialog Component                   - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function Legacy({
  onClose,
  recurring,
  defaultAccountId,
  focusSplits,
  wrapperId,
  isPrefilled,
  txnActionCallback,
  txn: txnProp,
  disableSalesReceiptFields,
  receiptId,
}: TransactionEditDialogProps) {
  const dispatchRef = useRef(false);
  const pendingForceResetFromQcsRef = useRef(false);
  const pendingMarkRecurringAfterSaveRef = useRef(false);
  const store = useAppStore();
  const categoryRef = useRef(txnProp?.coa || defaultTxn.coa); // used for switching between payment and income categories in QBP
  const dispatch = useDispatch();
  const { classes, cx } = useStyles();
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const { closeDialog } = useUrlDialog();

  const [txn, setTxn] = useState(txnProp || defaultTxn);
  const defaultBusinessId = useAppSelector((state) =>
    businessEnabled ? accountsSelectors.getAccountById(state, defaultAccountId)?.businessId : null,
  );
  const create = !transactionsUtils.isExistingTxn(txn);
  const isManual = txn.source === 'USER_ENTERED';

  const openAccounts = useAppSelector((state) =>
    accountsSelectors.getOpenOrSelectedCashAccounts(state, txn?.accountId || ''),
  );
  const allAccounts = useAppSelector(accountsSelectors.getResourcesById);
  const isTxnFromArAccount = useAppSelector(
    (state) => accountsSelectors.getAccountById(state, txn?.accountId)?.type === 'ACCOUNTS_RECEIVABLE',
  );
  const defaultId =
    txn.accountId ||
    (defaultAccountId && openAccounts.has(defaultAccountId) ? defaultAccountId : openAccounts?.first()?.id) ||
    '';

  const futureTxnsFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'futureTxns'));
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const incomeCOAIds = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const businessCOAIds = useAppSelector(categoriesSelectors.getBusinessCoaIdsSet);
  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);

  const [excludeIncomeByDefault] = usePreferenceV2('sSPExcludeIncomeByDefault');

  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const connectedTxn = useAppSelector((state) =>
    txn?.id ? transactionsSelectors.getTransactionById(state, txn.id) : undefined,
  );
  // The receipts service owns the receipt/transaction link, so resolve it by transaction id rather
  // than trusting the RECEIPT-sourced attachment — transaction writes can strip the attachment's
  // source, and the link must survive that.
  const hasReceiptAttachment = Boolean(txn.attachments?.some(transactionsTypes.isReceiptAttachment));
  const linkedReceipt = useAppSelector((state) =>
    receiptId
      ? receiptsSelectors.selectReceiptById(state, receiptId)
      : txn.id
        ? receiptsSelectors.selectReceiptByTransactionId(state, txn.id)
        : undefined,
  );
  const txnReceiptId = receiptId ?? linkedReceipt?.id;
  const shouldSyncReceiptData = useAppSelector(receiptsSelectors.selectShouldSyncReceiptData);
  const receiptsSynced = useAppSelector((state) => receiptsSelectors.selectLastSyncDate(state) !== null);
  const hasAnyAttachment = Boolean(txn.attachments?.length);
  const appDispatch = useAppDispatch();
  useEffect(() => {
    // Receipts are normally loaded by the Receipt Center route; fetch them here when a
    // receipt-linked transaction is opened before that route has been visited. Attachments can
    // lose their RECEIPT source on transaction writes, so before the first receipts sync any
    // attachment-bearing transaction is a fetch candidate.
    if (shouldSyncReceiptData && !txnReceiptId && (hasReceiptAttachment || (hasAnyAttachment && !receiptsSynced))) {
      appDispatch(receiptsActions.fetchReceipts(undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSyncReceiptData, hasReceiptAttachment, hasAnyAttachment, appDispatch]);

  const arAccountsByBusinessId = useAppSelector(accountsSelectors.getArAccountByBusiness);
  const arAccountIds = useAppSelector(accountsSelectors.getAllArAccountIds);

  const currentDate = useAppSelector(dateSelectors.getCurrentDate);

  // +++              State values                 +++
  // - - - - - - - - - - - - - - - - - - - - - - - - -

  // UI values
  const [open, setOpen] = useState(true);

  const [showUnsavedSplitsChanges, setShowUnsavedSplitsChanges] = useState(false);
  const [showUnsavedRecurringPrompt, setShowUnsavedRecurringPrompt] = useState(false);
  const [showRecurringOptions, setShowRecurringOptions] = useState(Boolean(recurring));
  const [recurringSelect, setRecurringSelect] = useState(false);
  const [editRecurring, setEditRecurring] = useState(false);
  const [editSeries, setEditSeries] = useState(false);
  const [editSeriesInitialTab, setEditSeriesInitialTab] = useState(0);
  const [editSeriesAssignSplits, setEditSeriesAssignSplits] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [txnClientId, setTxnClientId] = useState<string | null>(uuidv4().toUpperCase()); // client ID when doing recurring things on manual txn create

  const [waitingForTxn, setWaitingForTxn] = useState(false);
  const [waitingForTransfer, setWaitingForTransfer] = useState(false);
  const [completionText, setCompletionText] = useState<string | null>(null);

  const [modelIdToLink, setModelIdToLink] = useState<string | null | undefined>(null);

  const [otherTransfer, setOtherTransfer] = useState<null | CashFlowTransaction>(null);
  const [isDirty, setIsDirty] = useState(isPrefilled || false);

  // txForm values
  const [flag, setFlagInternal] = useState(txn.userFlag);
  const [reviewed, setReviewed] = useState(txn.isReviewed);
  const [accountId, setAccountInternal] = useState(defaultId);
  const [status, setStatusInternal] = useState(
    create ? (accountsUtils.isAccountConnected(openAccounts.get(defaultId)) && 'PENDING') || 'CLEARED' : txn.state,
  );

  const [date, setDateInternal] = useState(DateTime.fromISO(txn.postedOn));
  const [dateError, setDateError] = useState<string | undefined>();
  const dateInfoText = useMemo(() => {
    if (!futureTxnsFf) {
      return null;
    }

    if (date > currentDate) {
      return 'Future dates will create a Reminder.';
    }

    return null;
  }, [currentDate, date, futureTxnsFf]);

  const [payee, setPayeeInternal] = useState(txn.payee || '');
  const [amount, setAmountInternal] = useState(Number(txn.amount || 0));
  const [coa, setCoaInternal] = useState(txn.coa);
  const [checkNumber, setCheckNumberInternal] = useState(txn.check?.number || '');
  const [tags, setTagsInternal] = useState(txn.tags || []); // This is so the tag field is the same as the currentTxn tag field if undefined.
  const [excludeReports, setExcludeReportsInternal] = useState(
    txnProp ? Boolean(txn.isExcludedFromReports) : accountsById.get(defaultId)?.isExcludedFromReports || false,
  );
  const [excludeF2S, setExcludeF2SInternal] = useState(
    txnProp
      ? Boolean(txn.isExcludedFromF2S)
      : accountsById.get(defaultId)?.isExcludedFromBudgets ||
          transactionsUtils.isAutoExcludedFromF2S(txn.coa, excludeIncomeByDefault, incomeCOAIds, businessCOAIds) ||
          false,
  );
  const [excludeF2SOverridden, setExcludeF2SOverridden] = useState(false);
  const [memo, setMemoInternal] = useState(txn.memo || '');
  const [attachments, setAttachmentsInternal] = useState(txn.attachments);
  // Restore the RECEIPT source on the linked receipt's attachment when a write stripped it, so it
  // never renders as an empty document chip and stays typed on the next save.
  const attachmentsWithReceiptSources = useMemo(
    () => repairReceiptAttachmentSources(attachments, linkedReceipt),
    [attachments, linkedReceipt],
  );
  // splits are always records now for what i can tell but txn types has it as an object or record
  const [split, setSplitInternal] = useState(() =>
    txn.split ? ((Record.isRecord(txn.split) ? txn.split : Record(txn.split)) as RecordOf<SplitProps>) : null,
  );
  const [isBill, setIsBillInternal] = useState<boolean | undefined | null>(Boolean(txn.isBill));
  const [businessId, setBusinessIdInternal] = useState(create ? defaultBusinessId : txn.businessId);

  const isAssetPayment = useMemo(
    () => isArAssociatedPaymentTxn({ coa, split }, arAccountIds),
    [arAccountIds, coa, split],
  );

  const [customersAndProjects, setCustomersAndProjectsInternal] = useState<ProjectSelect>({
    customerId: txn.customerId ?? null,
    projectId: txn.projectId ?? null,
    billable: txn?.isBillable,
    payment: isAssetPayment,
  });

  const resetFormStateFromTxn = useCallback(
    (nextTxn: CashFlowTransaction) => {
      // Keep the local form state in sync with QCS updates when we *intend* to refresh the UI
      // (ex: unlinking from a series should revert the transaction and remove splits).
      setTxn(nextTxn);
      setFlagInternal(nextTxn.userFlag);
      setReviewed(nextTxn.isReviewed);
      setAccountInternal(nextTxn.accountId ?? '');
      setStatusInternal((nextTxn.state as 'CLEARED' | 'PENDING' | 'RECONCILED' | undefined) ?? 'CLEARED');
      setDateInternal(DateTime.fromISO(nextTxn.postedOn));
      setPayeeInternal(nextTxn.payee || '');
      setAmountInternal(Number(nextTxn.amount || 0));
      setCoaInternal(nextTxn.coa);
      setCheckNumberInternal(nextTxn.check?.number || '');
      setTagsInternal(nextTxn.tags || []);
      setExcludeReportsInternal(Boolean(nextTxn.isExcludedFromReports));
      setExcludeF2SInternal(Boolean(nextTxn.isExcludedFromF2S));
      setMemoInternal(nextTxn.memo || '');
      setAttachmentsInternal(nextTxn.attachments);
      setSplitInternal(() =>
        nextTxn.split
          ? ((Record.isRecord(nextTxn.split) ? nextTxn.split : Record(nextTxn.split)) as RecordOf<SplitProps>)
          : null,
      );
      setIsBillInternal(Boolean(nextTxn.isBill));
      setBusinessIdInternal(nextTxn.businessId ?? defaultBusinessId);
      const nextIsAssetPayment = isArAssociatedPaymentTxn(nextTxn, arAccountIds);
      setCustomersAndProjectsInternal({
        customerId: nextTxn.customerId ?? null,
        projectId: nextTxn.projectId ?? null,
        billable: nextTxn?.isBillable,
        payment: nextIsAssetPayment,
      });
      categoryRef.current = nextTxn.coa || defaultTxn.coa;
      setIsDirty(false);
    },
    [arAccountIds, defaultBusinessId],
  );

  function getTransactionDetailsMPProps(props: any): any {
    const { action, context, txn, ...otherProps } = props;
    const category = chartOfAccountsSelectors.getCoaStringSelector(null, txn?.coa, true);
    const type = txn.isBill
      ? 'BILL'
      : txn.isSubscription
        ? 'SUBSCRIPTION'
        : scheduledTransactionsUtils.typeFromCOA(txn?.coa ?? { id: '', type: 'NONE' });

    return {
      action: action,
      source: 'txnDetails',
      context,
      payee: txn?.payee,
      category,
      type: type,
      ...otherProps,
    };
  }

  const setExcludeReports = useCallback((val: boolean) => {
    setExcludeReportsInternal(val);
    setIsDirty(true);
  }, []);
  const setExcludeF2S = useCallback((val: boolean) => {
    setExcludeF2SOverridden(true);
    setExcludeF2SInternal(val);
    setIsDirty(true);
  }, []);

  const setCoa = useCallback(
    (val: ChartOfAccount | ChartOfAccountProps) => {
      const selectedAccount = accountId ? accountsById.get(accountId) : undefined;

      // If a txn was a category that WOULD be auto-excluded, we now auto-INCLUDE it when swapping to the new category
      const shouldAutoExclude = transactionsUtils.isAutoExcludedFromF2S(
        val,
        excludeIncomeByDefault,
        incomeCOAIds,
        businessCOAIds,
      );
      const wasAutoExcluded = transactionsUtils.isAutoExcludedFromF2S(
        coa,
        excludeIncomeByDefault,
        incomeCOAIds,
        businessCOAIds,
      );

      // Only preserve the user's explicit unchecked state when swapping between two income
      // categories.  Business categories are never user-configurable for spending-plan inclusion,
      // so any transition involving a business COA always force-excludes.
      const prevIsIncomeCoa = excludeIncomeByDefault && Boolean(incomeCOAIds?.has?.(coa?.id ?? ''));
      const nextIsIncomeCoa = excludeIncomeByDefault && Boolean(incomeCOAIds?.has?.(val?.id ?? ''));
      const isIncomeToIncomeTransition = prevIsIncomeCoa && nextIsIncomeCoa;

      if (shouldAutoExclude && !isIncomeToIncomeTransition) {
        setExcludeF2S(true);
      } else if (!shouldAutoExclude && !selectedAccount?.isExcludedFromBudgets && wasAutoExcluded) {
        setExcludeF2S(false);
      }

      if (chartOfAccountsUtils.isTransferCOA(val)) {
        setExcludeReports(true);
      } else if (!selectedAccount?.isExcludedFromReports && chartOfAccountsUtils.isTransferCOA(coa)) {
        setExcludeReports(false);
      }
      if (businessEnabled) {
        // When setting to an AR account, save the current category as the restore point.
        // When the user picks a regular category, that new category becomes the restore point.
        categoryRef.current = arAccountIds.includes(val?.id) ? coa : val;
        setCustomersAndProjectsInternal((v) => ({ ...v, payment: arAccountIds.includes(val?.id) }));
      }
      setCoaInternal(val);
      setIsDirty(true);
    },
    [
      accountId,
      accountsById,
      excludeIncomeByDefault,
      incomeCOAIds,
      businessCOAIds,
      coa,
      businessEnabled,
      setExcludeF2S,
      setExcludeReports,
      arAccountIds,
    ],
  );
  const resetCustomersAndProjects = useCallback(() => {
    setCustomersAndProjectsInternal({
      customerId: null,
      projectId: null,
      billable: false,
      payment: isAssetPayment,
    });
  }, [isAssetPayment]);

  const invoicesLength = useAppSelector((state) => getInvoices(state).length);

  const disabledPayment = useCallback(
    (bId?: string | null) =>
      (bId && (!arAccountsByBusinessId?.has(bId) || invoicesLength === 0)) ||
      !(accountId && accountsUtils.doesAccountSupportPaymentSrcs(allAccounts.get(accountId))),
    [arAccountsByBusinessId, invoicesLength, accountId, allAccounts],
  );

  const setBusinessId = useCallback(
    (businessIdParam: string | undefined) => {
      const newAr = businessIdParam ? arAccountsByBusinessId.get(businessIdParam) : undefined;
      if (isAssetPayment) {
        if (disabledPayment(businessIdParam)) {
          setCoa({ id: '0', type: 'UNCATEGORIZED' });
        } else if (newAr?.id && coa?.id !== newAr?.id) {
          setCoa({ id: newAr.id, type: 'ACCOUNT' });
        }
      }
      setBusinessIdInternal(businessIdParam);
      setIsDirty(true);
    },
    [setBusinessIdInternal, setCoa, coa, arAccountsByBusinessId, isAssetPayment, disabledPayment],
  );

  const showPaymentDialogLink = useAppSelector(
    (state) => isAssetPayment && getTxnTransferHasAllocations(state, { transferId: txn?.transfer?.id }),
  );

  // condense form values into Immutable and dirty check
  // not memoizing because it basically needs to re-do on every meaningful state change
  const tempTxn = connectedTxn || txn;
  const transactionsById = useAppSelector(transactionsSelectors.getResourcesById);
  const scheduledTransactionsById = useAppSelector(scheduledTransactionsSelectors.getResourcesById);
  const hasSplitTransferCounterpart = useMemo(
    () =>
      isAnySplitTransferCounterpart({
        txn: tempTxn,
        transactionsById,
        scheduledModelTxnsById: scheduledTransactionsById,
      }),
    [scheduledTransactionsById, tempTxn, transactionsById],
  );
  const excludeF2SDisplayFromTxn = useMemo(
    () =>
      getExcludedFromSpendingPlanDisplayState(
        {
          isExcludedFromF2S: excludeF2S,
          split: split ?? undefined,
        },
        transactionsById,
      ),
    [excludeF2S, split, transactionsById],
  );
  const excludeF2SDisplay = useMemo(() => {
    if (excludeF2SOverridden) {
      return { value: excludeF2S as boolean, indeterminate: false };
    }
    return excludeF2SDisplayFromTxn;
  }, [excludeF2S, excludeF2SDisplayFromTxn, excludeF2SOverridden]);

  const excludeReportsDisplayFromTxn = useMemo(
    () =>
      getExcludedFromReportsDisplayState(
        {
          isExcludedFromReports: excludeReports,
          split: split ?? undefined,
        },
        transactionsById,
      ),
    [excludeReports, split, transactionsById],
  );
  const spreadTxn = tempTxn.toJS ? (tempTxn.toJS() as TransactionProps) : tempTxn;

  // Server-owned billable linkage (`linkedItem`) cleanup — mirror of the V2 dialog fix.
  // 1) reconcile the working split's linkedItem to the latest server copy (`spreadTxn` = connectedTxn),
  // 2) strip stale links only when the loaded invoice/estimate no longer backs them, so a
  //    date-filtered store does not clear a valid billed-expense link.
  const linkedInvoiceIndex = useAppSelector(transactionsSelectors.getLinkedInvoiceIndexIfLoaded);
  // `tempTxn` (connectedTxn || txn) is the server copy and a stable reference, so the memo only
  // recomputes when the working split, server copy, or invoice index actually change.
  const cleanedSplit = useMemo(() => {
    if (!split) return null;
    const cleaned = stripDeadBillableLinks(
      reconcileSplitLinkedItems(split.toJS() as SplitProps, tempTxn.split),
      linkedInvoiceIndex,
    );
    return cleaned ? transactionsTypes.mkSplit(cleaned) : null;
  }, [split, tempTxn, linkedInvoiceIndex]);

  // Unsaved-changes check ignores the server-owned `linkedItem` (it drifts as the server bills/
  // unbills the expense and is never a user edit) — otherwise it pops an update prompt on close when the
  // user changed nothing.
  const splitHasUnsavedChanges =
    !_.isEqual(omitLinkedItemsFromSplit(split), omitLinkedItemsFromSplit(txn.split)) || isDirty;
  const formattedDate = date?.isValid ? date.toISODate() : spreadTxn.postedOn;
  const formattedAmount = Number(amount?.toFixed?.(2));
  const finalTags = List.isList(tags) ? tags : [];
  const finalMemo = spreadTxn.memo !== '' && memo === '' ? undefined : memo;
  const finalCoa = split ? null : coa;
  const finalProjectId = split ? null : customersAndProjects.projectId;
  const finalCustomerId = split ? null : customersAndProjects.customerId;
  const finalIsBillable = isAssetPayment ? false : customersAndProjects.billable;

  const newTxnObj: TransactionProps = {
    ...spreadTxn,
    accountId,
    state: status,
    postedOn: formattedDate,
    payee,
    amount: formattedAmount,
    coa: finalCoa,
    businessId,
    customerId: finalCustomerId,
    projectId: finalProjectId,
    isBillable: finalIsBillable,
    tags: finalTags,
    isExcludedFromReports: excludeReports,
    isExcludedFromF2S: excludeF2S,
    memo: finalMemo,
    attachments: attachmentsWithReceiptSources,
    split: cleanedSplit,
    isBill,
    userFlag: flag,
    isReviewed: reviewed,
    check: {
      ...spreadTxn.check,
      number: checkNumber,
    },
  };

  const submittedTxn = transactionsTypes.mkCashFlowTransaction(newTxnObj);
  const currentTxn = hasSplitTransferCounterpart
    ? preserveSplitTransferGuardedFields({ previous: tempTxn, next: submittedTxn })
    : submittedTxn;

  const ornamentType = useOrnamentType({
    transaction: currentTxn,
    coa,
    linkedItem: currentTxn?.linkedItem,
    type: currentTxn?.type,
    amount,
    businessId,
    create,
  });

  const hideCreateRule =
    isInvoiceTxn(txn) ||
    isRefundTxn(txn) ||
    isUITransferTxn(txn) ||
    isManualTransaction(txn) ||
    disableSalesReceiptFields;

  // Keep a stable identity for hooks deps (used by SplitDialog) while still using latest values.
  const customerProjectContextRef = useRef({
    amount,
    businessId,
    ornamentType,
    arAccountsByBusinessId,
    coa,
  });
  customerProjectContextRef.current = {
    amount,
    businessId,
    ornamentType,
    arAccountsByBusinessId,
    coa,
  };

  const setBusinessIdRef = useRef(setBusinessId);
  setBusinessIdRef.current = setBusinessId;
  const setCoaRef = useRef(setCoa);
  setCoaRef.current = setCoa;

  const setCustomersAndProjects = useCallback(
    (res: ProjectSelect) => {
      const {
        businessId: latestBusinessId,
        ornamentType: latestOrnamentType,
        coa: latestCoa,
      } = customerProjectContextRef.current;

      // Update usage type to match default of client.
      if (res.customerId) {
        const customer = customersSelectors.getCustomerById(store.getState(), res.customerId);
        const project = res.projectId ? projectsSelectors.getProjectById(store.getState(), res.projectId) : null;
        const newBizId = project?.businessId || customer?.businessId;
        if (newBizId) {
          const category = latestCoa?.id ? categoriesSelectors.getCategoryById(store.getState(), latestCoa.id) : null;
          setBusinessIdRef.current(newBizId);
          if (latestCoa?.type !== 'ACCOUNT' && category?.usageType !== 'BUSINESS') {
            setCoaRef.current({ id: '0', type: 'UNCATEGORIZED' });
          }
        }
      }

      if (latestBusinessId && latestOrnamentType === 'payment') {
        const businessArAccount = customerProjectContextRef.current.arAccountsByBusinessId.get(latestBusinessId);
        if (businessArAccount && businessArAccount.id && res.payment) {
          setCoaRef.current({ id: businessArAccount.id, type: 'ACCOUNT' });
        } else if (
          (categoryRef.current?.id && categoryRef.current.id === businessArAccount?.id) ||
          (businessArAccount && !res.payment && !categoryRef.current)
        ) {
          setCoaRef.current({ id: '0', type: 'UNCATEGORIZED' });
        } else if (categoryRef.current) {
          setCoaRef.current(categoryRef.current);
        }
      }

      setCustomersAndProjectsInternal({
        ...res,
      });
      setIsDirty(true);
    },
    [store],
  );

  const showCheckNumberField = useAppSelector((state) =>
    accountsSelectors.shouldAccountDisplayCheckNumberField(state, accountId),
  );

  const showPaymentDialog =
    businessEnabled &&
    isAssetPayment &&
    (spreadTxn?.coa?.id !== currentTxn?.coa?.id || !_.isEqual(tempTxn?.split, newTxnObj?.split) || create);

  const linkedExpense =
    txn.subtype !== 'REFUND' &&
    (!!newTxnObj.linkedItem?.id || newTxnObj.split?.items?.some((split) => !!split.linkedItem?.id));
  // Note: `linkedExpense` (split lines assigned to an invoice) is intentionally NOT gated here.
  // The SplitDialog's internal `disabledQBP` already locks invoice-sensitive fields (category,
  // amount, client/project, billable, add/remove split, delete all) when any split has a
  // linkedItem, but keeps tags and notes editable so users can still update those metadata
  // fields after invoicing. Forcing read-only mode here would replace the Continue button with
  // a non-saving Close button, causing tag/note edits to be silently discarded.
  const splitDialogReadOnly = Boolean(
    (businessEnabled && isAssetPayment && arAccountIds?.includes(coa?.id)) || disableSalesReceiptFields,
  );

  const currentSplitPayments = useAppSelector((state) => getTotalNumberOfSplitsWithAllocations(state, { txn }));

  const fullDisableQBP =
    disableSalesReceiptFields ||
    showPaymentDialogLink ||
    linkedExpense ||
    (txn.type === 'PAYMENT' && isAssetPayment) ||
    currentSplitPayments > 0 ||
    txn.subtype === 'SALES_RECEIPT';

  // const isDirty = !_.isEqualWith(spreadTxn, currentTxn.toJS(), compareFunc);

  const validPayee = Boolean(payee && payee !== '');

  // #  Function declarations to update the reviewed Field
  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  const disableReports = finalCoa === null ? false : chartOfAccountsUtils.disableExcludeReports(finalCoa, split);

  const updateReviewed = useCallback(() => {
    setReviewed(!reviewed);
    setIsDirty(true);
  }, [reviewed]);

  const setFlag = useCallback((val: QcsColor | null | undefined) => {
    setFlagInternal(val);
    setIsDirty(true);
  }, []);

  const setAccount = useCallback(
    (val: string) => {
      const previousAccount = accountId ? accountsById.get(accountId) : undefined;
      const selectedAccount = val ? accountsById.get(val) : undefined;

      if (selectedAccount?.isExcludedFromReports) {
        setExcludeReports(true);
      } else if (!chartOfAccountsUtils.isTransferCOA(coa) && previousAccount?.isExcludedFromReports) {
        setExcludeReports(false);
      }
      const coaIsAutoExcludedFromF2S = transactionsUtils.isAutoExcludedFromF2S(
        coa,
        excludeIncomeByDefault,
        incomeCOAIds,
        businessCOAIds,
      );
      if (selectedAccount?.isExcludedFromBudgets) {
        setExcludeF2S(true);
      } else if (!coaIsAutoExcludedFromF2S && previousAccount?.isExcludedFromBudgets) {
        setExcludeF2S(false);
      }

      setAccountInternal(val);
      setIsDirty(true);
      if (create) {
        setStatusInternal(
          accountsUtils.isAccountConnected(accountsSelectors.getAccountById(undefined, val)) ? 'PENDING' : 'CLEARED',
        );
      }
    },
    [
      accountsById,
      coa,
      create,
      setExcludeF2S,
      setExcludeReports,
      accountId,
      excludeIncomeByDefault,
      incomeCOAIds,
      businessCOAIds,
    ],
  );

  const setStatus = useCallback((val: 'CLEARED' | 'PENDING' | 'RECONCILED') => {
    setStatusInternal(val);
    setIsDirty(true);
  }, []);

  const setDate = useCallback((val: DateTime) => {
    setDateInternal(val);
    setIsDirty(true);
  }, []);

  const setPayee = useCallback((val: string) => {
    setPayeeInternal(val);
    setIsDirty(true);
  }, []);

  const setAmount = useCallback((val: number) => {
    setAmountInternal(val);
    setIsDirty(true);
  }, []);

  const setTags = useCallback((val: List<Tag>) => {
    setTagsInternal(val);
    setIsDirty(true);
  }, []);

  const setMemo = useCallback((val: string) => {
    setMemoInternal(val);
    setIsDirty(true);
  }, []);

  const setAttachments = useCallback((val: Attachment[]) => {
    setAttachmentsInternal(val);
    setIsDirty(true);
  }, []);

  const setSplit = useCallback((val: RecordOf<SplitProps> | null) => {
    setSplitInternal(val);
    setIsDirty(true);
  }, []);

  const setIsBill = useCallback((val: boolean) => {
    setIsBillInternal(val);
    setIsDirty(true);
  }, []);

  // +++                  Data                     +++
  // - - - - - - - - - - - - - - - - - - - - - - - - -

  const model = useAppSelector((state) =>
    txn.stModelId ? scheduledTransactionsSelectors.getScheduledTransactionById(state, txn.stModelId) : undefined,
  );
  const incomeCOAs = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const isIncome = transactionsUtils.isIncomeFromTxn(currentTxn, incomeCOAs);
  const isCredit = useAppSelector((state) => chartOfAccountsSelectors.isCreditBill(state, accountId, coa));
  const isTransfer = transactionsUtils.isUITransferTxn(currentTxn) && !isCredit;
  const createdTxn = useAppSelector((state) =>
    accountId && txnClientId
      ? transactionsSelectors.getCreatedTxnByClientAndAccountId(state, txnClientId, accountId)
      : undefined,
  );

  const refundTracker = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'refundTracker'));

  const hideClientsAndProjects = newTxnObj.split?.items;
  const seriesTxn = model?.transaction ?? null;
  const hasSplitSeries = Boolean(
    splitSeriesFf && txn.stModelId && seriesTxn && transactionsUtils.isSplitTxn(seriesTxn) && seriesTxn.split?.items,
  );

  // # ===================================================================================== #
  // # -----                             Action Functions                              ----- #
  // # ===================================================================================== #

  const updateCOA = useCallback(
    (newCOA: ChartOfAccountProps) => {
      setCoa(newCOA);
      if (create && newCOA) {
        const newIsIncome = transactionsUtils.isIncomeFromTxn({ ...txn, isBill, coa: newCOA }, incomeCOAs);
        if (isIncome !== newIsIncome) {
          // if coa type change, apply default sign
          const newAmount = Math.abs(amount) * (newIsIncome ? 1 : -1);
          setAmount(newAmount);
        }
      }
    },
    [setCoa, create, txn, isBill, incomeCOAs, isIncome, amount, setAmount],
  );

  const selectAccount = useCallback(
    (selectedAccount: AccountProps | undefined) => {
      setAccount(selectedAccount?.id || '');
      const nextBusinessId = accountsUtils.getBusinessIdForAccountSelection({
        account: selectedAccount,
        businessEnabled,
      });
      if (nextBusinessId !== undefined) {
        setBusinessId(nextBusinessId);
        if (!nextBusinessId) {
          resetCustomersAndProjects();
        }

        const nextCoa = resolveCoaForBusinessChange({
          coa,
          categoriesById,
          accountsById,
          nextBusinessId,
        });
        if (nextCoa) {
          setCoa(nextCoa);
        }
      }
    },
    [accountsById, businessEnabled, categoriesById, coa, resetCustomersAndProjects, setAccount, setBusinessId, setCoa],
  );

  const updateAmount = useCallback(
    (e: { target: { value: string } }) => {
      const newAmount = Number(e.target.value);
      setAmount(newAmount);
    },
    [setAmount],
  );

  // no memo callback because this uses currentTxn
  const exitRecurringCreate = useCallback(() => {
    setEditRecurring(false);
    if (editRecurring && model) {
      dispatch(
        scheduledTransactionsActions.deleteScheduledTransaction(
          scheduledTransactionsTypes.mkScheduledTransaction({
            id: model.id,
            ...(model.toJS?.() as ScheduledTransactionType),
            isUserVerified: true,
          }),
        ),
      );
      setTxn(txn.merge({ stModelId: undefined, isBill: false }));
      setIsBillInternal(false);
      setIsDirty(true);
    }
  }, [dispatch, editRecurring, model, txn]);

  // always new so memo unnecessary
  const resetToEdit = useCallback(() => {
    setShowRecurringOptions(false);
    setRecurringSelect(false);
    exitRecurringCreate();
  }, [exitRecurringCreate]);

  // always new so memo unnecessary
  const closeAll = useCallback(
    (isSuccess?: boolean) => {
      log.log('\nclosingAll');
      setOpen(false);
      resetToEdit();
      onClose?.(isSuccess);
      closeDialog(`TRANSACTION_DETAILS_${txnProp?.id || '0'}`);
      dispatch(rootUiActions.removeDialog(`TRANSACTION_DETAILS_${txnProp?.id || '0'}`));
    },
    [closeDialog, dispatch, onClose, resetToEdit, txnProp?.id],
  );

  const { performTransactionAction, saveTransactions, deleteTransactions, unMatchTransaction } = useTxnActions({
    wrapperId: wrapperId || 'TRANSACTION_DETAILS_DIALOG',
    robotMode: showPaymentDialog || showPaymentDialogLink, // don't show transfer help for payment dialog
    callback: (ret: ResourceTxnAction[]) => {
      txnActionCallback?.(ret);
      // on creation, wait to show payment dialog until txn is saved
      if (ret) {
        if (pendingMarkRecurringAfterSaveRef.current) {
          pendingMarkRecurringAfterSaveRef.current = false;
          setShowRecurringOptions(true);
        }
        const txnId = currentTxn?.id ?? ret.find((res) => res.clientId === txnClientId)?.id;
        if (showPaymentDialog) {
          dispatch(
            rootUiActions.createDialog(
              rootUiTypes.mkRootUiData({
                id: 'mark-as-payment',
                type: CONFIRMATION_DIALOG_TYPE,
                props: ImmutableMap(
                  mkConfirmationDialogProps({
                    title: <Typography variant='titleMedium'>Record invoice payment</Typography>,
                    content: (
                      <Box className={classes.dialogContent}>
                        <Box>
                          <img src={InvoiceZeroSvg} alt='Invoice Illustration' className={classes.dialogIllustration} />
                        </Box>
                        <Box className={classes.dialogContentTextWrapper}>
                          <Typography variant='titleMedium'>You got paid!</Typography>
                          <Typography variant='bodyRegular'>
                            Let&apos;s get this applied to your invoice to mark it as paid and reduce the amount in your
                            accounts receivable account.
                          </Typography>
                        </Box>
                      </Box>
                    ),
                    confirmLabel: 'Apply Payment',
                    confirmAction: () => {
                      closeAll();
                      dispatch(
                        InvoicePaymentUpsertAction({
                          action: 'EDIT_ALLOCATIONS',
                          context: 'details-dialog',
                          initIdType: 'PAYMENT',
                          initId: txnId,
                          initScreen: currentTxn?.split?.items ? 'SPLIT' : 'MAIN',
                        }),
                      );
                    },
                    denyLabel: 'Not Now',
                    denyAction: () => {
                      closeAll();
                    },
                  }),
                ),
              }),
            ),
          );
        }
      }
    },
  });

  function exitUnsavedDialog() {
    setShowUnsavedSplitsChanges(false);
    setShowUnsavedRecurringPrompt(false);
  }

  function showUnsavedDialog() {
    setShowUnsavedRecurringPrompt(false);
    setShowUnsavedSplitsChanges(true);
  }

  function showUnsavedRecurringDialog() {
    setShowUnsavedRecurringPrompt(true);
    setShowUnsavedSplitsChanges(true);
  }

  const onDelete = () => {
    deleteTransactions([txn]);
    closeAll();
  };

  const onSave = () => {
    // exclude flags are automatically set based on the category change within saveTransactions, but the user could have changed them manually (to override defaults)
    // so we pass them explicitly to saveTransactions, which will apply the overrides after applying the category-based defaults (SIMPL-23930)
    saveTransactions([create ? currentTxn.set('clientId', txnClientId) : currentTxn], {
      txnOverrides: {
        isExcludedFromF2S: currentTxn.isExcludedFromF2S,
        isExcludedFromReports: currentTxn.isExcludedFromReports,
      },
    });

    if (!showPaymentDialog) closeAll(true);
  };

  const dialogId = 'add-memorized-rule';
  const handleClose = () => {
    dispatch(rootUiActions.removeDialog(dialogId));
    onClose?.();
  };

  const onCreateRule = () => {
    isDirty ? onSave() : closeAll();

    dispatch(
      TransactionRuleAction({
        id: dialogId,
        transaction: currentTxn,
        handleClose,
        originalIsReviewed: txn.isReviewed,
      }),
    );

    tracker.track(tracker.events.txnRuleStarted, { source: 'txnDetails' });
  };

  const supportedTransfer = useMemo(() => txn.coa && txn.coa.type === 'ACCOUNT' && isSupportedTransferTxn(txn), [txn]);
  const isMatched = useMemo(
    () => transactionsUtils.getMatchState(txn) === 'matched' && txn.source !== 'SCHEDULED_TRANSACTION',
    [txn],
  );
  const showMatchOrTransferRow = isMatched || supportedTransfer;

  const goToOtherSide = useCallback((foundTxn: CashFlowTransaction) => {
    setWaitingForTransfer(true);
    setTimeout(() => {
      setOtherTransfer(foundTxn);
      setWaitingForTransfer(false);
    }, 500);
  }, []);

  // #   Recurring Options
  // - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const unlinkRecurring = useCallback(() => {
    tracker.track(
      tracker.events.txnUpdatedInFTSRegister,
      getTransactionDetailsMPProps({ action: 'unlinkRecurring', context: 'details-dialog', txn }),
    );

    if (txn.stModelId) {
      // Figma note: when a transaction is unlinked from a series, any series-applied splits should be removed
      // and the transaction should be reverted. We accomplish this by force-resetting local form state to the
      // latest QCS transaction record once it arrives.
      if (splitSeriesFf) {
        pendingForceResetFromQcsRef.current = true;
      }
      performTransactionAction(
        { action: 'unlink-to-scheduled-txn', id: txn.id },
        // @ts-expect-error not ts
        { undo: { userMessage: 'Transaction unlinked.' } },
      );
      // todo : once matchedTxnId is set
      //  if matched or source === 'SCHEDULED_TRANSACTION', replace txn ID with that so that we go to the bank-downloaded txn
      setTxn(txn.set('stModelId', undefined));
      setIsBill(false);
    }
  }, [performTransactionAction, setIsBill, splitSeriesFf, txn]);

  const unMarkAsBill = useCallback(() => setIsBill(false), [setIsBill]);

  const markRecurring = useCallback(() => {
    if (!create && isDirty) {
      showUnsavedRecurringDialog();
      return;
    }
    setShowRecurringOptions(true);
  }, [create, isDirty]);

  const stopRecurring = useCallback(() => setShowRecurringOptions(false), []);

  const createTxn = () => {
    log.log('\nCreating txn!');
    setWaitingForTxn(true);
    const clientId = uuidv4().toUpperCase();
    setTxnClientId(clientId);
    saveTransactions([currentTxn.set('clientId', clientId)]);
  };

  const startRecurringSelect = () => {
    setRecurringSelect(true);
  };
  const exitRecurringSelect = () => {
    setRecurringSelect(false);
  };
  const createRecurring = () => {
    exitRecurringSelect();
    if (create) {
      createTxn();
    } else {
      setEditRecurring(true);
      const payload = { clientId: uuidv4().toUpperCase(), id: txn.id || '' };
      dispatch(
        scheduledTransactionsActions.createBillScoutSchedule(payload, {
          props: {
            sourceIsExcludedFromF2S: excludeF2S,
            sourceIsExcludedFromReports: excludeReports,
            sourceBusinessId: businessId ?? undefined,
          },
        }),
      );
    }
  };
  const showSplitDialogForTransaction = useCallback(
    (transaction: CashFlowTransaction) => {
      if (hasSplitTransferCounterpart) {
        return;
      }

      const dialogId = `SPLIT_DIALOG_${transaction.id || txnClientId || '0'}`;
      resetCustomersAndProjects();
      dispatch(
        showSplitDialog({
          id: dialogId,
          txn: transaction,
          setSplit,
          setAmount,
          setCoa: updateCOA,
          setTags,
          accountId,
          disabledPaymentFn: disabledPayment,
          setCustomersAndProjects,
          readOnly: splitDialogReadOnly,
          onBack: () => dispatch(rootUiActions.removeDialog(dialogId)),
          onClose: () => dispatch(rootUiActions.removeDialog(dialogId)),
        }),
      );
    },
    [
      accountId,
      disabledPayment,
      dispatch,
      hasSplitTransferCounterpart,
      resetCustomersAndProjects,
      splitDialogReadOnly,
      setAmount,
      setCustomersAndProjects,
      setSplit,
      setTags,
      txnClientId,
      updateCOA,
    ],
  );

  const openSplitDialog = useCallback(() => {
    showSplitDialogForTransaction(currentTxn);
  }, [currentTxn, showSplitDialogForTransaction]);

  const openReceiptSplits = useCallback(
    (seed: ReceiptSplitSeed): void => {
      const split = {
        ...seed.split,
        items: applyDefaultBusinessIdToSplitItems(seed.split.items, currentTxn.businessId),
      };
      showSplitDialogForTransaction(
        currentTxn.merge({
          amount: seed.amount,
          coa: null,
          split,
        }) as CashFlowTransaction,
      );
    },
    [currentTxn, showSplitDialogForTransaction],
  );

  const hasSplitItemsDefined = splitItemsToArray(split?.items ?? []).length > 0;

  /** Per-split lines: parent checkboxes reflect aggregate of child flags (checked / indeterminate). */
  const visibilityF2SDisplay = useMemo(
    () => (hasSplitItemsDefined ? excludeF2SDisplayFromTxn : excludeF2SDisplay),
    [excludeF2SDisplay, excludeF2SDisplayFromTxn, hasSplitItemsDefined],
  );
  const visibilityExcludeReportsDisplay = useMemo(
    () =>
      hasSplitItemsDefined ? excludeReportsDisplayFromTxn : { value: excludeReports, indeterminate: false as const },
    [excludeReports, excludeReportsDisplayFromTxn, hasSplitItemsDefined],
  );

  const openSplitDialogFromExclusionsBanner = useCallback(() => {
    openSplitDialog();
    tracker.track(tracker.events.viewSplitsV2, {
      initiator: txn.id === null ? 'createTxn' : 'editTxnWithSplits',
      transactionAmount: amount,
      transactionAmountSign: amount >= 0 ? 'positive' : 'negative',
    });
  }, [amount, openSplitDialog, txn.id]);

  const editSplitsFromExcludeBannerDisabled =
    linkedExpense || (txn.type === 'PAYMENT' && isAssetPayment) || txn.subtype === 'SALES_RECEIPT';

  const discardAndMarkRecurring = useCallback(() => {
    exitUnsavedDialog();
    resetFormStateFromTxn(connectedTxn || txn);
    setShowRecurringOptions(true);
  }, [connectedTxn, resetFormStateFromTxn, txn]);

  const updateAndMarkRecurring = useCallback(() => {
    exitUnsavedDialog();
    pendingMarkRecurringAfterSaveRef.current = true;
    saveTransactions([create ? currentTxn.set('clientId', txnClientId) : currentTxn], {
      txnOverrides: {
        isExcludedFromF2S: currentTxn.isExcludedFromF2S,
        isExcludedFromReports: currentTxn.isExcludedFromReports,
      },
    });
  }, [create, currentTxn, saveTransactions, txnClientId]);

  const saveRecurring = () => {
    log.debug('\nSaving recurring');
    setEditRecurring(false);
    setShowRecurringOptions(false);
    setRecurringSelect(false);
    setShowConfirmation(true);
  };

  const updateRecurring = () => {
    log.debug('\nUpdating recurring');
    setShowConfirmation(true);
    setEditRecurring(false);
    setEditSeries(false);
    setEditSeriesAssignSplits(false);
  };

  const closeRecurring = (dirty: boolean, force: boolean) => {
    if (force) {
      closeAll(); // recurring series was deleted, close all the modals
    } else {
      // recurring series dialog was closed, return to the confirmation dialog
      setShowConfirmation(true);
      setEditRecurring(false);
      setEditSeries(false);
      setEditSeriesAssignSplits(false);
    }
  };

  const onCloseReview = () => {
    setShowConfirmation(false);
    setEditRecurring(false);
    setEditSeries(false);
    setEditSeriesAssignSplits(false);
    closeAll();
  };

  const linkTxn = (recurringId: string | undefined) => {
    const txnToUse = createdTxn || currentTxn;
    const modelId = recurringId || modelIdToLink;
    if (txnToUse?.id && modelId) {
      log.log('\nLinking txn');
      setModelIdToLink(null);
      setWaitingForTxn(false);
      setCompletionText('Transaction linked to recurring series');
      performTransactionAction(
        { action: 'link-to-scheduled-txn', id: txnToUse.id, otherId: modelId },
        // @ts-expect-error not ts
        { undo: { userMessage: 'Transaction linked.' } },
      );
    }
  };
  const linkRecurring = (item: ScheduledTransaction) => {
    log.log('\nLinking recurring');
    tracker.track(
      tracker.events.txnUpdatedInFTSRegister,
      getTransactionDetailsMPProps({ action: 'linkRecurring', context: 'details-dialog', txn: currentTxn }),
    );

    if (create) {
      createTxn();
      setModelIdToLink(item.id);
    } else {
      linkTxn(item.id);
    }
    resetToEdit();
  };
  const doOneTimeBill = () => {
    setCompletionText('Marked as one time bill');
    setIsBill(true);
    // initiate save one render later so that loading/confirmation dialog can display smoothly
    setTimeout(() => saveTransactions([currentTxn.set('isBill', true)]), 0);
  };

  // # ===================================================================================== #
  // # -----                                  Effects                                  ----- #
  // # ===================================================================================== #

  // forward to the right 'recurring' option if provided
  useEffect(() => {
    if (typeof recurring === 'string') {
      if (recurring === 'create') createRecurring();
      if (recurring === 'link') startRecurringSelect();
    }
  }, [recurring]); // eslint-disable-line react-hooks/exhaustive-deps

  // waiting for QCS processor (only in create)
  useEffect(() => {
    if (
      create &&
      createdTxn?.id &&
      transactionsUtils.isExistingTxn(createdTxn) &&
      waitingForTxn &&
      !dispatchRef?.current
    ) {
      // found txn
      dispatchRef.current = true; // Used to prevent bump update from triggering this function twice
      setTxn(createdTxn);
      setWaitingForTxn(false);
      setTxnClientId(null);
      if (modelIdToLink) {
        // if link - link txn
        linkTxn(modelIdToLink);
      } else {
        // do bill scout
        setEditRecurring(true);
        const payload = { clientId: uuidv4().toUpperCase(), id: createdTxn.id };
        dispatch(
          scheduledTransactionsActions.createBillScoutSchedule(payload, {
            props: {
              sourceIsExcludedFromF2S: excludeF2S,
              sourceIsExcludedFromReports: excludeReports,
              sourceBusinessId: businessId ?? undefined,
            },
          }),
        );
      }
    }
  }, [createdTxn]); // eslint-disable-line react-hooks/exhaustive-deps

  // update txn when we get a new record from QCS
  useEffect(() => {
    if (connectedTxn) {
      log.info('new QCS txn', connectedTxn?.toJS?.());
      // if just unlinked a scheduled transaction and QCS restored the reminder, switch the UI over to display
      // the just-unlinked bank-downloaded txn
      const matchedTxnId = connectedTxn.matchedTxn?.id;
      if (connectedTxn.source === 'SCHEDULED_TRANSACTION_PENDING' && matchedTxnId && connectedTxn.id !== matchedTxnId) {
        log.info('reminder restored, switching to txn with ID:', matchedTxnId);
        const bankTxn = transactionsSelectors.getTransactionById(store.getState(), matchedTxnId);
        if (bankTxn && pendingForceResetFromQcsRef.current) {
          resetFormStateFromTxn(bankTxn);
          pendingForceResetFromQcsRef.current = false;
        } else {
          setTxn(txn.set('id', matchedTxnId));
        }
      } else {
        // this is likely after a standard link-to-recurring or an unlink (that wasn't a matched txn that QCS auto-restored as a reminder)
        log.info('updating to new QCS txn');
        if (pendingForceResetFromQcsRef.current) {
          resetFormStateFromTxn(connectedTxn);
          pendingForceResetFromQcsRef.current = false;
        } else {
          setTxn(connectedTxn);
          if (connectedTxn.stModelId) {
            // update bill flag if has modelId
            setIsBillInternal(connectedTxn.isBill);
          }
        }
      }
    } else if (!create && waitingForTxn) {
      // txn was deleted after being auto-matched (usually after link to recurring)
      log.log('Txn was auto-deleted after link to recurring!\nLooking for auto-matched reminder');
      // We may not need this anymore as we close the modal after completing recurring options now, but I'll leave this in
      //  for now because it does lessen the chance of the user trying to update a deleted txn
      const state = store?.getState();
      const autoMatchedTxn =
        state && currentTxn.id && txn?.accountId
          ? transactionsSelectors.getTxnByMatchedTxnAndAccountId(state, currentTxn.id, txn.accountId)
          : undefined;
      if (autoMatchedTxn) {
        // found reminder, so update txn with reminder info.
        log.log('Found reminder!\nSwitching to matched txn');
        setWaitingForTxn(false);
        setTxn(autoMatchedTxn);
        setIsBillInternal(autoMatchedTxn.isBill);
      } else {
        // txn was deleted but cannot find reminder it matched to
        log.warn(
          'Looked for auto-matched reminder after txn was deleted, but could not find it!\n' +
            'Closing dialog to prevent editing a deleted txn',
        );
        closeAll();
      }
    }
  }, [connectedTxn]); // eslint-disable-line react-hooks/exhaustive-deps

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  // # ===================================================================================== #
  // # -----                                Renderers                                  ----- #
  // # ===================================================================================== #

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const cpSection = useMemo(() => {
    const { cpData, type } = txn;
    if (!cpData) {
      return null;
    }
    // Only show this for payment transactions when the series-splits UI is enabled.
    if (!splitSeriesFf && type === 'PAYMENT') {
      return null;
    }
    const bankName = accountsUtils.getAccountString(txn.accountId);

    let displayDate;
    if (cpData.postedOn) {
      displayDate = DateTime.fromFormat(cpData.postedOn || '', 'yyyy-M-d');
    } else {
      displayDate = DateTime.fromISO(txn.postedOn);
    }

    return (
      <Typography variant='captionRegular' color='textSecondary' display='block' className={classes.spacerRow}>
        {`Appears on your ${bankName} statement as `}
        <Link className={classes.link} id='statement-name-link' onClick={() => clickPayeeLink(cpData.payee)}>
          {cpData.payee}
          <OpenNewIcon fontSize='small' className={classes.openIcon} />
        </Link>
        {` on ${displayDate.toLocaleString(DateTime.DATE_MED)}. `}
      </Typography>
    );
  }, [classes, splitSeriesFf, txn]);

  const accountRow = useMemo(
    () => (
      <div id='amount-account-row'>
        <div className={classes.thinRow}>
          {(split?.items || hasSplitSeries) && !hasSplitTransferCounterpart ? (
            <QPanelButton
              id='edit-split-amount'
              disabled={linkedExpense || (txn.type === 'PAYMENT' && isAssetPayment) || txn.subtype === 'SALES_RECEIPT'}
              className={classes.editSplitAmount}
              onClick={() => {
                openSplitDialog();
                tracker.track(tracker.events.viewSplitsV2, {
                  initiator: txn.id === null ? 'createTxn' : 'editTxnWithSplits',
                  transactionAmount: amount,
                  transactionAmountSign: amount >= 0 ? 'positive' : 'negative',
                });
              }}
              title={
                <QAmountField
                  label='Amount'
                  value={amount}
                  showSign='positive'
                  showColor='positive'
                  inputId='details-amount'
                />
              }
              arrow={
                <IconButton size='small'>
                  <ChevronRightIcon fontSize='small' />
                </IconButton>
              }
            />
          ) : (
            <QAmountField
              label='Amount'
              margin='none'
              className={classes.column}
              textFieldVariant='filled'
              editable
              submitOnBlur
              value={amount}
              onChange={updateAmount}
              onSubmit={updateAmount}
              expectedSign={isIncome ? 'positive' : 'negative'}
              id='details-amount'
              disabled={fullDisableQBP || hasSplitTransferCounterpart}
            />
          )}

          <AccountField
            initialAccountId={accountId}
            accounts={showPaymentDialogLink || txn.type === 'PAYMENT' ? allAccounts : openAccounts}
            onSelected={selectAccount}
            label='Account'
            variant='filled'
            margin='none'
            className={classes.column}
            readOnly={!isManual || hasSplitTransferCounterpart}
            id='account-select'
            disabled={fullDisableQBP || hasSplitTransferCounterpart}
          />
          {txn.paymentTxnType ? (
            <TextField
              id='payment-type-field'
              label='Type'
              variant='filled'
              value={txn.paymentTxnType}
              disabled={true}
            />
          ) : null}
        </div>

        {showMatchOrTransferRow && (
          <div className={classes.refundLine}>
            <Typography variant='captionRegular'>
              {isMatched ? (
                <>
                  {txn.source === 'USER_ENTERED'
                    ? 'A manual and bank transaction were combined to create this entry. '
                    : 'A pending and cleared transaction were combined to create this entry. '}
                  <Link
                    className={classes.link}
                    onClick={() => {
                      unMatchTransaction(txn);
                    }}
                    id='unmerge-button'
                  >
                    Unmerge
                  </Link>
                  <br />
                </>
              ) : null}
              {/** Show other side field here if not a split txn. */}
              {supportedTransfer ? (
                <OtherSideField
                  coa={txn.coa}
                  transferId={txn.transfer?.id}
                  txnId={txn.id ?? txn.clientId}
                  goToOtherSide={goToOtherSide}
                />
              ) : null}
            </Typography>
          </div>
        )}
      </div>
    ),
    [
      accountId,
      allAccounts,
      classes,
      hasSplitSeries,
      hasSplitTransferCounterpart,
      isIncome,
      isMatched,
      isManual,
      linkedExpense,
      isAssetPayment,
      txn,
      amount,
      updateAmount,
      fullDisableQBP,
      openAccounts,
      showPaymentDialogLink,
      split,
      selectAccount,
      showMatchOrTransferRow,
      supportedTransfer,
      goToOtherSide,
      unMatchTransaction,
      openSplitDialog,
    ],
  );

  // not memoizing because the amountField can change it's internal state and that wants to be preserved (not removed on a memoized rerender)
  const payeeRow = (
    <div id='payee-date-row' className={classes.thinRow}>
      <SPayeeField
        id='payee-field-editable'
        defaultValue={payee}
        onChange={setPayee}
        className={classes.payeeField}
        popupIcon={null}
        autoFocus={!focusSplits}
      />

      <DateField
        date={date}
        onChange={setDate}
        onError={(error: string | undefined) => {
          setDateError(error);
        }}
        maxDate={futureTxnsFf ? DateTime.now().plus({ year: 1 }) : undefined}
        className={classes.dateField}
        variant='filled'
        allowInvalid
        error={!!dateError}
        helperText={dateError || dateInfoText || ''}
        disabled={fullDisableQBP || hasSplitTransferCounterpart}
        // textFieldProps={{ // remove margin on error to keep vertical position
        //   style: { marginBottom: date?.isValid ? 8 : 0, marginTop: 16 },
        // }}
      />
    </div>
  );

  const paymentId = txn.cpData?.cpPaymentIntentId;

  const bottomSection = useMemo(
    () => (
      <div className={classes.bottomSection}>
        <div id='notes-and-attachments-set' className={cx(classes.column, classes.notesAndAttachmentsSetWidth)}>
          {paymentId ? (
            <TextField
              id='payment-id-field'
              label='Payment ID'
              value={paymentId}
              className={classes.paymentId}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      size='small'
                      onClick={() => {
                        navigator.clipboard.writeText(paymentId);
                        dispatchSimpleNotification('Payment ID copied to clipboard');
                      }}
                    >
                      <FileCopyOutlinedIcon fontSize='inherit' />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : null}
          <BoxField
            id='memo-field'
            label='Note'
            placeholder='Add your note here'
            value={memo}
            onChange={setMemo}
            inputProps={{
              maxLength: 512,
            }}
            margin='dense'
          />

          <AttachmentsField
            id='attachments-field'
            attachments={attachmentsWithReceiptSources}
            onChange={(_event: any, newAttachments: Attachment[], _reason: any, _details: any) =>
              setAttachments(newAttachments)
            }
            box
            margin='dense'
          />
          {showCheckNumberField ? (
            <TextField
              label='Check #'
              value={checkNumber}
              onChange={(e) => {
                setCheckNumberInternal(e.target.value);
                setIsDirty(true);
              }}
              variant='filled'
            />
          ) : null}
        </div>
        <VisibilitySection
          className={classes.visibilitySection}
          disabledReports={disableReports}
          excludeF2S={visibilityF2SDisplay.indeterminate ? null : visibilityF2SDisplay.value}
          setExcludeF2S={setExcludeF2S}
          indeterminateF2S={visibilityF2SDisplay.indeterminate}
          excludeReports={
            disableReports
              ? true
              : visibilityExcludeReportsDisplay.indeterminate
                ? null
                : visibilityExcludeReportsDisplay.value
          }
          setExcludeReports={setExcludeReports}
          indeterminateReports={visibilityExcludeReportsDisplay.indeterminate}
          managedPerSplit={hasSplitItemsDefined}
          onEditSplits={
            hasSplitItemsDefined && !hasSplitTransferCounterpart ? openSplitDialogFromExclusionsBanner : undefined
          }
          editSplitsDisabled={
            hasSplitItemsDefined && !hasSplitTransferCounterpart ? editSplitsFromExcludeBannerDisabled : undefined
          }
        />
      </div>
    ),
    [
      classes,
      cx,
      memo,
      setMemo,
      attachmentsWithReceiptSources,
      showCheckNumberField,
      checkNumber,
      disableReports,
      setExcludeF2S,
      setExcludeReports,
      paymentId,
      setAttachments,
      hasSplitItemsDefined,
      hasSplitTransferCounterpart,
      visibilityExcludeReportsDisplay,
      visibilityF2SDisplay,
      openSplitDialogFromExclusionsBanner,
      editSplitsFromExcludeBannerDisabled,
    ],
  );

  // +++            Recurring Section              +++
  // - - - - - - - - - - - - - - - - - - - - - - - - -

  // not memoizing because it needs 'doOneTimeBill' which requires currentTxn which changes on every state change
  const renderRecurringOptions = () => {
    let recurringName;
    let copyText;
    if (isTransfer) {
      recurringName = 'transfer';
      copyText = 'transfers';
    } else {
      recurringName = isIncome ? 'income' : 'bill or subscription';
      copyText = isIncome ? 'income' : 'bills and subscriptions';
    }

    return (
      <div className={classes.recurringRoot}>
        <QIllustration name='upcoming' size='md' />

        <div className={classes.recurringColumn}>
          <Typography className={classes.recurringSubtitle}>
            {`Linking transactions to recurring ${copyText} helps with planning and tracking payments in Spending Plan.` +
              ' (You can manually link to an existing recurrence if we weren’t able to auto-match it.)'}
          </Typography>

          <QPanelButton
            title={`Create a new recurring ${recurringName}`}
            subtitle='Set the frequency to plan for upcoming payments.'
            startIcon={<RepeatIcon />}
            onClick={createRecurring}
            id='create-new-recurring-button'
          />
          <QPanelButton
            title={`Link to existing ${recurringName}`}
            subtitle='Assign this transaction to a recurrence that’s already set up.'
            startIcon={<LinkIcon />}
            onClick={startRecurringSelect}
            id='link-existing-button'
          />

          {!isIncome && !isTransfer && (
            <QPanelButton
              title='Mark as one-time bill'
              subtitle='No future payments, but added to bills in your Spending Plan.'
              startIcon={<RepeatOneIcon />}
              onClick={doOneTimeBill}
              hideArrow
              id='mark-as-bill-button'
            />
          )}
        </div>
      </div>
    );
  };

  // # ===================================================================================== #
  // # -----                               Main Return                                 ----- #
  // # ===================================================================================== #

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  let title = create ? 'Create transaction' : 'Transaction detail';
  if (showRecurringOptions) {
    if (isIncome) {
      title = 'Mark as recurring income';
    } else {
      title = 'Mark as bill or recurring';
    }
    if (isTransfer) {
      title = 'Mark as recurring transfer';
    }
  }

  const showEditRecurring = Boolean(showRecurringOptions && editRecurring);
  const showRecurringSelect = Boolean(showRecurringOptions && recurringSelect);

  const clientSelectedPayment =
    customersAndProjects.billable || (ornamentType === 'payment' && customersAndProjects.payment)
      ? Boolean(customersAndProjects.customerId)
      : true;

  const validAttachments = !attachments || !attachments.find((item) => item && !item.id);
  const completeEnough = Boolean(
    validPayee && accountId && date?.isValid && validAttachments && !dateError && clientSelectedPayment,
  );

  const isAccountClosed = accountId ? openAccounts?.get(accountId)?.isClosed : undefined;

  const waiting = waitingForTxn || waitingForTransfer || (editRecurring && !model);

  let loadingLine;
  if (waitingForTransfer) {
    loadingLine = 'Finding transfer';
  } else {
    loadingLine =
      modelIdToLink || (!create && waitingForTxn) ? 'Assigning to recurring series' : 'Creating recurring series';
  }

  const splitsDisabled = Boolean(
    (businessEnabled && isAssetPayment && arAccountIds?.includes(coa?.id)) || linkedExpense,
  );

  let deleteQTip = '';
  if (showPaymentDialogLink) {
    deleteQTip = 'Transaction can only be deleted after the payment assignment is removed';
  } else if (linkedExpense) {
    deleteQTip = 'This expense has been billed and cannot be deleted';
  } else if (currentSplitPayments > 0) {
    deleteQTip = 'This split transaction can only be deleted after the payment assignment is removed';
  }

  const previousTransaction = usePrevious(connectedTxn);
  const [isDirtyOutside, setIsDirtyOutside] = useState(false);
  useEffect(() => {
    if (previousTransaction && connectedTxn !== previousTransaction) {
      setIsDirtyOutside(true);
    }
  }, [connectedTxn, previousTransaction]);
  const onRefreshClick = useCallback(() => {
    closeAll();
    if (connectedTxn) {
      setTimeout(() => {
        dispatch(
          showTxnDetailsDialog({
            onClose,
            recurring,
            defaultAccountId,
            focusSplits,
            wrapperId,
            txnActionCallback,
            txn: connectedTxn,
          }),
        );
      }, 0);
    } else {
      dispatchSimpleNotification('Transaction synced');
    }
  }, [
    closeAll,
    connectedTxn,
    defaultAccountId,
    dispatch,
    focusSplits,
    onClose,
    recurring,
    txnActionCallback,
    wrapperId,
  ]);

  return otherTransfer ? (
    <Legacy txn={otherTransfer} onClose={closeAll} />
  ) : (
    <>
      <StdDialog
        disableRestoreFocus
        onBack={stopRecurring}
        onClose={splitHasUnsavedChanges ? showUnsavedDialog : closeAll} // ignores server-owned linkedItem; see splitHasUnsavedChanges above
        open={
          open &&
          !editSeries &&
          !showEditRecurring &&
          !showRecurringSelect &&
          !waiting &&
          !completionText &&
          !showConfirmation
        }
        sharedcomponentid='TRANSACTION_DETAILS_DIALOG'
        showBackButton={showRecurringOptions}
        size='md'
        title={title}
      >
        {isTxnFromArAccount ? (
          // If we reach Ar txn hide it with zero state. Should never happen.
          <ZeroStateView
            button={
              <Button id='try-again-button' variant='outlined' onClick={() => closeAll}>
                Go Back
              </Button>
            }
            id='failed-to-fetch-accounts'
            title='Oops'
            description='Looks like you tried to access a read-only transaction.'
            qIllustration='accounts'
          />
        ) : (
          <>
            <Dump obj={{ isDirty, completeEnough, currentTxn, txn }} />
            <Collapse in={isDirtyOutside}>
              <Alert
                severity='info'
                action={
                  <Button
                    id='refresh-to-sync-button'
                    endIcon={<RefreshRoundedIcon />}
                    size='small'
                    onClick={onRefreshClick}
                  >
                    Refresh to sync
                  </Button>
                }
                sx={{ borderRadius: 0 }}
              >
                New edits were made to this transaction. Refresh to update.
              </Alert>
            </Collapse>

            {showRecurringOptions ? (
              renderRecurringOptions()
            ) : (
              <StdDialogContent
                id='transaction-details-content'
                className={classes.content}
                sx={{
                  // Flex child of dialog paper: allow shrinking so scroll stays on this pane (not the paper edge).
                  minHeight: 0,
                }}
              >
                <TxnHeaderSection
                  cpSection={cpSection}
                  txn={txn}
                  model={model}
                  create={create}
                  status={status}
                  isBill={isBill}
                  unMarkAsBill={unMarkAsBill}
                  markRecurring={markRecurring}
                  setFlag={setFlag}
                  flag={flag}
                  disabledRecurring={!completeEnough || isAccountClosed || fullDisableQBP}
                  viewRecurring={() => dispatch(showViewSeriesDialog(txn))}
                  setEditSeries={() => setEditSeries(true)}
                  updateReviewed={updateReviewed}
                  reviewed={reviewed}
                  setStatus={setStatus}
                  unlinkRecurring={unlinkRecurring}
                  disabled={fullDisableQBP}
                />

                <div className={classes.dividerSection}>
                  <div className={classes.divider} />
                </div>

                {/* Payee + Amount row */}
                {payeeRow}
                {/* Account, status, date row */}
                {accountRow}
                {/* Transfer line buttons */}
                {/* Refunds + Category + Tags row */}
                {refundTracker && !hasSplitTransferCounterpart && (
                  <div className={cx(classes.refundLine, classes.refundAfterAmountRow)} id='refund-line'>
                    <RefundLine
                      txn={currentTxn}
                      disabled={Boolean(showPaymentDialogLink || linkedExpense || disableSalesReceiptFields)}
                    />
                  </div>
                )}
                {txnReceiptId ? (
                  <ReceiptSection
                    receiptId={txnReceiptId}
                    hasSplits={hasSplitItemsDefined}
                    onCreateSplits={openReceiptSplits}
                  />
                ) : null}
                <CategorySection
                  accountId={accountId}
                  coa={coa}
                  setCoa={updateCOA}
                  setTags={setTags}
                  amount={amount}
                  businessId={businessId}
                  setBusinessId={setBusinessId}
                  customersAndProjects={customersAndProjects}
                  resetCustomersAndProjects={resetCustomersAndProjects}
                  validPayee={validPayee}
                  onOpenSplitDialog={openSplitDialog}
                  txn={currentTxn}
                  showSplitRow={Boolean(refundTracker) && !txnReceiptId && !hasSplitTransferCounterpart}
                  splitsDisabled={splitsDisabled || !!disableSalesReceiptFields || hasSplitTransferCounterpart}
                  disabled={Boolean(
                    showPaymentDialogLink || linkedExpense || disableSalesReceiptFields || hasSplitTransferCounterpart,
                  )}
                  tagsDisabled={Boolean(showPaymentDialogLink || linkedExpense || disableSalesReceiptFields)}
                  goToOtherSide={goToOtherSide}
                  disabledPayment={disabledPayment(businessId)}
                />
                {/* Divider */}
                {businessEnabled ? (
                  <>
                    {!hideClientsAndProjects && (
                      <ClientProjectSelect
                        value={customersAndProjects}
                        onChange={setCustomersAndProjects}
                        className={classes.clientProjectSelect}
                        ornamentType={ornamentType}
                        disabledInvoicePayment={disabledPayment(businessId) || disableSalesReceiptFields}
                        disabledSelection={
                          showPaymentDialogLink ||
                          linkedExpense ||
                          currentSplitPayments > 0 ||
                          hasSplitTransferCounterpart ||
                          disableSalesReceiptFields
                        }
                        invoiceId={currentTxn?.linkedItem?.id}
                        paymentAssetId={currentTxn?.id}
                        onButtonClick={closeAll}
                      />
                    )}
                  </>
                ) : null}
                <div className={classes.dividerSection}>
                  <div className={classes.divider} />
                </div>
                {/* Visibility + Info section */}
                {bottomSection}
              </StdDialogContent>
            )}
            {!showRecurringOptions && (
              <ConnectionStatusFooter>
                <DialogActions>
                  {!create && (
                    <Box sx={{ flex: 1 }}>
                      <QTip title={deleteQTip}>
                        <div>
                          <QButton
                            id='delete-txn'
                            aria-label='delete-txn'
                            onClick={onDelete}
                            disabled={create || fullDisableQBP}
                            disableOutline
                            className={classes.deleteButton}
                          >
                            Delete transaction
                          </QButton>
                        </div>
                      </QTip>
                    </Box>
                  )}

                  <DevMenuButton>
                    <MenuItem
                      onClick={() =>
                        dispatchRequestDialog({
                          method: 'PUT',
                          path: `/transactions/${currentTxn.id}`,
                          body: JSON.stringify(currentTxn, null, 2),
                        })
                      }
                    >
                      Manual Transaction API Call
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        dispatchGeneratorDialog({
                          txns: [currentTxn],
                          start: currentTxn.postedOn,
                        })
                      }
                    >
                      Generate Multiple Downloaded Transactions
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        dispatchGeneratorDialog({
                          txns: [
                            currentTxn.toJS(),
                            {
                              ...currentTxn.toJS(),
                              accountId: currentTxn.coa?.id,
                              coa: { ...(currentTxn.toJS().coa as any), id: currentTxn.accountId },
                              amount: -currentTxn.amount,
                            },
                          ],
                          start: currentTxn.postedOn,
                        })
                      }
                    >
                      Generate Multiple Transfer Transactions
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        dispatchGeneratorDialog({
                          txns: [currentTxn],
                          start: currentTxn.postedOn,
                          end: currentTxn.postedOn,
                        })
                      }
                    >
                      Generate 1 Downloaded Transaction
                    </MenuItem>
                  </DevMenuButton>

                  {!hideCreateRule && (
                    <QButton
                      type='submit'
                      id='save-txn'
                      className={classes.createRuleButton}
                      onClick={onCreateRule}
                      disabled={!completeEnough}
                    >
                      {isDirty ? 'Update & Create Rule' : 'Create Rule'}
                    </QButton>
                  )}

                  <QButton
                    type='submit'
                    id='save-txn'
                    variant='contained'
                    disabled={!completeEnough || !isDirty}
                    onClick={onSave}
                    className={classes.updateButton}
                  >
                    {create ? 'Create' : 'Update'}
                  </QButton>
                </DialogActions>
              </ConnectionStatusFooter>
            )}
          </>
        )}
      </StdDialog>

      {showUnsavedSplitsChanges && (
        <UnsavedChangesDialog
          onClose={exitUnsavedDialog}
          open={open && showUnsavedSplitsChanges && !waiting}
          closeAll={showUnsavedRecurringPrompt ? discardAndMarkRecurring : closeAll}
          update={showUnsavedRecurringPrompt ? updateAndMarkRecurring : onSave}
          notComplete={!completeEnough}
          create={create}
          continueAfterDiscard={showUnsavedRecurringPrompt}
          continueAfterUpdate={showUnsavedRecurringPrompt}
        />
      )}

      {showEditRecurring &&
        !waiting &&
        model && ( // create recurring
          <ScheduledTransactionEditDialog
            fakeCreate
            open={showEditRecurring && !waiting}
            scheduledTransaction={model}
            onSubmit={saveRecurring}
            onClose={closeAll}
            onBack={exitRecurringCreate}
            title='Create series'
          />
        )}

      {showConfirmation && !showEditRecurring && (
        <ReviewDialog
          open
          model={model}
          onClose={onCloseReview}
          edit={(initialTab = 0) => {
            setShowConfirmation(false);
            setEditRecurring(false);
            setEditSeriesInitialTab(initialTab);
            setEditSeriesAssignSplits(false);
            setEditSeries(true);
          }}
          sharedcomponentid='SCHEDULED_TRANSACTION_ADD_DIALOG'
        />
      )}

      {editSeries &&
        model && ( // update recurring
          <ScheduledTransactionEditDialog
            open={Boolean(model) && editSeries}
            key={model ? model.clientId || model.id : 'noKey'}
            scheduledTransaction={model}
            onClose={closeRecurring}
            onSubmit={updateRecurring}
            initialTab={editSeriesInitialTab}
            initialShowAssignSplits={editSeriesAssignSplits}
          />
        )}

      {showRecurringSelect && (
        <SelectScheduledTransactionDialog
          open={!waiting}
          onClose={closeAll}
          onSelected={linkRecurring}
          onCreate={createRecurring}
          showBackButton
          onBack={exitRecurringSelect}
          txn={currentTxn}
        />
      )}

      {(waiting || completionText) && (
        <LoadingDialog
          open
          onClose={closeAll}
          loading={waiting}
          loadingString={loadingLine}
          successString={completionText}
        />
      )}
    </>
  );
}

export default React.memo(Legacy);
